import { CircleUserRound, LogOut, Monitor, Moon, Sun } from 'lucide-react';
import { useRef } from 'react';
import { Link } from 'react-router-dom';

import { config } from '@/config/env';
import { consoleCanonicalOwnerLabels } from '@/navigation/consoleNavigation';
import { consoleRoutePaths } from '@/navigation/consoleRoutePaths';
import { visibleUserRole } from '@/pages/admin/users/userPresentation';
import type { User } from '@/types/auth';
import { cx, focusRingClass, interactiveClass } from '@/components/ui/shared';
import { DARK_THEME_AVAILABLE, type ThemePreference } from '@/theme/themePreference';
import { useThemePreference } from '@/theme/useThemePreference';

const themeOptions: Array<{ value: ThemePreference; label: string; icon: typeof Sun }> = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'system', label: 'Match system', icon: Monitor },
];

interface ConsoleAccountMenuProps {
  activeScopeLabel: string;
  onLogout: () => Promise<void>;
  user: User;
}

export default function ConsoleAccountMenu({
  activeScopeLabel,
  onLogout,
  user,
}: ConsoleAccountMenuProps) {
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const close = () => detailsRef.current?.removeAttribute('open');
  const { preference, setPreference } = useThemePreference();

  return (
    <details ref={detailsRef} className="relative">
      <summary
        className={cx(
          focusRingClass,
          'flex min-h-control-comfortable cursor-pointer list-none items-center gap-2 rounded-control border border-border-subtle bg-surface px-2 text-ink hover:border-border-strong hover:bg-surface-recessed sm:px-3',
        )}
        aria-label="Open account menu"
      >
        <CircleUserRound className="h-5 w-5" aria-hidden="true" />
        <span className="hidden max-w-32 truncate text-control font-semibold sm:block">
          {user.username}
        </span>
      </summary>
      <div className="absolute right-0 z-40 mt-2 w-[min(20rem,calc(100vw-1rem))] rounded-overlay border border-border-subtle bg-surface-raised p-2 text-ink shadow-overlay">
        <div className="border-b border-border-subtle px-3 py-3">
          <p className="truncate text-control font-semibold text-ink-strong">
            {user.full_name || user.username}
          </p>
          <p className="mt-1 truncate text-meta text-ink-muted">{user.email}</p>
          <dl className="mt-3 grid gap-1 text-meta">
            <div className="flex justify-between gap-3">
              <dt className="text-ink-muted">Role</dt>
              <dd className="text-right font-semibold">{visibleUserRole(user.role)}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-ink-muted">Scope</dt>
              <dd className="truncate text-right font-semibold">{activeScopeLabel}</dd>
            </div>
          </dl>
          {config.environment === 'development' ? (
            <p className="mt-3 rounded-control border border-status-blocked-border bg-status-blocked-surface px-2 py-1 text-meta font-semibold text-status-blocked">
              LAB_DEV · not production evidence
            </p>
          ) : null}
        </div>
        {DARK_THEME_AVAILABLE ? (
        <fieldset className="border-b border-border-subtle px-3 py-3">
          <legend className="float-left mb-2 text-meta font-semibold text-ink-muted">
            Appearance
          </legend>
          <div className="clear-both grid grid-cols-3 gap-1 rounded-control bg-surface-recessed p-1">
            {themeOptions.map(({ value, label, icon: Icon }) => (
              <label
                key={value}
                className={cx(
                  interactiveClass,
                  'flex min-h-10 cursor-pointer flex-col items-center justify-center gap-0.5 rounded-control px-1 text-meta font-semibold has-[:focus-visible]:shadow-focus',
                  preference === value
                    ? 'bg-surface-raised text-ink-strong shadow-raised'
                    : 'text-ink-muted hover:text-ink',
                )}
              >
                <input
                  type="radio"
                  name="console-theme"
                  value={value}
                  checked={preference === value}
                  onChange={() => setPreference(value)}
                  className="sr-only"
                />
                <Icon className="h-4 w-4" aria-hidden="true" />
                {label}
              </label>
            ))}
          </div>
        </fieldset>
        ) : null}
        <div className="grid gap-1 pt-2">
          <Link
            to={consoleRoutePaths.profile}
            onClick={close}
            className={cx(
              focusRingClass,
              'min-h-control-comfortable rounded-control px-3 py-2 text-control font-semibold hover:bg-surface-recessed',
            )}
          >
            {consoleCanonicalOwnerLabels.profile}
          </Link>
          <button
            type="button"
            onClick={() => {
              close();
              void onLogout();
            }}
            className={cx(
              focusRingClass,
              'flex min-h-control-comfortable items-center gap-2 rounded-control px-3 py-2 text-left text-control font-semibold text-action-danger hover:bg-status-failed-surface',
            )}
          >
            <LogOut className="h-4 w-4" aria-hidden="true" />
            Sign out
          </button>
        </div>
      </div>
    </details>
  );
}
